export const NEW_VERSION_AVAILABLE_EVENT = "vts-new-version-available";

const VERSION_SERVICE_WORKER_URL = "/version-service-worker.js";
const VERSION_MANIFEST_URL = "/version.json";
const VERSION_CHECK_INTERVAL_MS = 60_000;
const LOG_BADGE =
  "background:#0a0a0a;color:#fff;padding:2px 7px;border-radius:999px;font-weight:700";
const LOG_OK = "color:#22c55e;font-weight:700";
const LOG_INFO = "color:#06b6d4;font-weight:700";
const LOG_WARN = "color:#f59e0b;font-weight:700";
const currentAppVersion =
  typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "dev";

type VersionManifest = {
  version?: string;
  buildTime?: string;
};

type WorkerVersionMessage = {
  type?: string;
  version?: string;
  buildTime?: string;
};

type NewVersionAvailableDetail = {
  version: string;
};

declare global {
  interface WindowEventMap {
    [NEW_VERSION_AVAILABLE_EVENT]: CustomEvent<NewVersionAvailableDetail>;
  }
}

let hasRegistered = false;
let hasBoundChecks = false;
let checkInFlight = false;
let reloadRequested = false;
let reloadInProgress = false;
let waitingWorker: ServiceWorker | null = null;
let waitingVersion: string | null = null;

function log(step: string, detail?: unknown, level: "ok" | "info" | "warn" = "info") {
  const detailStyle = level === "ok" ? LOG_OK : level === "warn" ? LOG_WARN : LOG_INFO;
  console.log("%cVTS update%c " + step, LOG_BADGE, detailStyle, detail ?? "");
}

function logError(step: string, error: unknown) {
  console.warn("%cVTS update%c " + step, LOG_BADGE, LOG_WARN, error);
}

function readWorkerVersion(worker: ServiceWorker | null) {
  return new Promise<WorkerVersionMessage | null>((resolve) => {
    if (!worker) {
      resolve(null);
      return;
    }

    const channel = new MessageChannel();
    const timer = window.setTimeout(() => resolve(null), 1500);

    channel.port1.onmessage = (event: MessageEvent<WorkerVersionMessage>) => {
      window.clearTimeout(timer);
      resolve(event.data);
    };

    try {
      worker.postMessage({ type: "GET_VERSION" }, [channel.port2]);
    } catch (error) {
      window.clearTimeout(timer);
      logError("worker version probe failed", error);
      resolve(null);
    }
  });
}

async function announceWaitingWorker(worker: ServiceWorker, reason: string) {
  if (waitingWorker === worker) return;

  waitingWorker = worker;
  const workerVersion = await readWorkerVersion(worker);
  waitingVersion = workerVersion?.version || "new deployment";

  log(
    "new version waiting",
    {
      reason,
      currentVersion: currentAppVersion,
      waitingVersion,
      waitingBuildTime: workerVersion?.buildTime,
    },
    "ok",
  );

  window.dispatchEvent(
    new CustomEvent<NewVersionAvailableDetail>(NEW_VERSION_AVAILABLE_EVENT, {
      detail: { version: waitingVersion },
    }),
  );
}

async function checkVersionManifest() {
  try {
    const response = await fetch(VERSION_MANIFEST_URL, {
      cache: "no-store",
      headers: {
        "Cache-Control": "no-cache",
      },
    });

    if (!response.ok) {
      log("manifest check skipped", { status: response.status }, "warn");
      return null;
    }

    const manifest = (await response.json()) as VersionManifest;
    log(
      "manifest check",
      {
        currentVersion: currentAppVersion,
        remoteVersion: manifest.version,
        remoteBuildTime: manifest.buildTime,
        match: manifest.version === currentAppVersion,
      },
      manifest.version === currentAppVersion ? "ok" : "warn",
    );

    return manifest;
  } catch (error) {
    logError("manifest check failed", error);
    return null;
  }
}

async function checkForServiceWorkerUpdate(
  registration: ServiceWorkerRegistration,
  reason: string,
) {
  if (checkInFlight) {
    log("check already running", { reason }, "warn");
    return;
  }

  checkInFlight = true;
  log("checking", { reason, currentVersion: currentAppVersion });

  try {
    await checkVersionManifest();
    await registration.update();
    log("service worker update check complete", { reason }, "ok");
  } catch (error) {
    logError("service worker update check failed", error);
  } finally {
    checkInFlight = false;
  }
}

function bindVersionChecks(registration: ServiceWorkerRegistration) {
  if (hasBoundChecks) return;
  hasBoundChecks = true;

  const runCheck = (reason: string) => {
    void checkForServiceWorkerUpdate(registration, reason);
  };

  runCheck("startup");

  window.setInterval(() => {
    if (document.visibilityState === "visible") runCheck("visible interval");
  }, VERSION_CHECK_INTERVAL_MS);

  window.addEventListener("focus", () => runCheck("window focus"));
  window.addEventListener("online", () => runCheck("browser online"));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") runCheck("tab visible");
  });
}

export function getPendingVersionUpdate() {
  return waitingVersion;
}

export function activatePendingVersionUpdate() {
  if (!waitingWorker) {
    log("refresh fallback", "No waiting worker is available yet.", "warn");
    window.location.reload();
    return;
  }

  log("activating waiting worker", { waitingVersion }, "ok");
  reloadRequested = true;

  try {
    waitingWorker.postMessage({ type: "SKIP_WAITING" });
  } catch (error) {
    logError("activation message failed", error);
    window.location.reload();
  }
}

export async function registerVersionServiceWorker() {
  if (hasRegistered) return;
  hasRegistered = true;

  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    log("unsupported", "Service workers are not available in this browser.", "warn");
    return;
  }

  if (!window.isSecureContext) {
    log("blocked", "Service workers require HTTPS or localhost.", "warn");
    return;
  }

  log("boot", { currentVersion: currentAppVersion });

  try {
    const registration = await navigator.serviceWorker.register(
      VERSION_SERVICE_WORKER_URL,
      { scope: "/" },
    );

    log(
      "registered",
      {
        scope: registration.scope,
        currentVersion: currentAppVersion,
      },
      "ok",
    );

    const activeVersion = await readWorkerVersion(registration.active);
    if (activeVersion?.version) {
      log("active worker", activeVersion, "ok");
    }

    if (registration.waiting && navigator.serviceWorker.controller) {
      void announceWaitingWorker(registration.waiting, "already waiting");
    }

    registration.addEventListener("updatefound", () => {
      const installingWorker = registration.installing;
      log("update found", { hasInstallingWorker: Boolean(installingWorker) }, "ok");

      if (!installingWorker) return;

      installingWorker.addEventListener("statechange", () => {
        log("worker state", { state: installingWorker.state });

        if (installingWorker.state !== "installed") return;

        if (navigator.serviceWorker.controller) {
          void announceWaitingWorker(installingWorker, "installed");
          return;
        }

        log("first install ready", { currentVersion: currentAppVersion }, "ok");
      });
    });

    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (!reloadRequested) {
        log("controller changed", "A worker claimed the page without a reload request.");
        return;
      }

      log("controller changed", "Reloading into the latest deployment.", "ok");
      if (reloadInProgress) return;
      reloadInProgress = true;
      window.location.reload();
    });

    bindVersionChecks(registration);
  } catch (error) {
    logError("registration failed", error);
  }
}
